import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import Modal from '../shared/Modal';
import FileUpload from '../shared/FileUpload';
import { useToast } from '../ui/Toast';
import WordImport from './WordImport';
import PolicyEditor from './PolicyEditor';
import HtmlDocumentView from './HtmlDocumentView';
import FeatureTour from '../shared/FeatureTour';
import useFeatureTour from '../../hooks/useFeatureTour';
import { QMS_CONTROL_TOUR_ID, qmsControlTourSteps } from './qmsControlTour';
import { QMS_DOC_TYPE_OPTIONS, docTypeLabel } from '../../utils/qmsDocTypes';
// What the server takes for each kind of file (pen test AD25996): the pickers offer
// only that, and a refusal by name is said beside the picker before anything is sent.
import { acceptAttribute, uploadRefusal, UPLOAD_KINDS } from '../../../../src/lib/fileSafety.js';

// Edit content: create the draft from the live version (or re-open the one
// already open), then fetch its bodies for the editor.
async function openDraftForEditing(docId) {
  const version = await api.post(`qms/documents/${docId}/versions/from-live`, {});
  const content = await api.get(`qms/documents/${docId}/versions/${version.id}/content`);
  return { version, content };
}

const CONTENT_KIND_LABEL = { html: 'HTML', file: 'PDF + Word' };

// Going live used to be a confirm() that silently reset every acknowledgement.
// The reset is now a choice, explained: a real change means everyone reads and
// confirms again; a minor correction keeps the existing acknowledgements and
// re-points them at the new version.
function GoLiveModal({ doc, version, onClose, onDone }) {
  const { toast } = useToast();
  const [reset, setReset] = useState(true);
  const [busy, setBusy] = useState(false);
  const needsAck = !!doc.requires_acknowledgement;

  const go = async () => {
    setBusy(true);
    try {
      await api.put(`qms/documents/${doc.id}/versions/${version.id}/go-live`, { reset_acknowledgements: needsAck ? reset : true });
      toast(`Version ${version.version_number} is now live`, 'success');
      onDone();
    } catch (e) {
      toast(e.message || 'Could not publish', 'error');
      setBusy(false);
    }
  };

  return (
    <Modal title={`Go live — ${doc.doc_number} v${version.version_number}`} onClose={onClose}
      footer={<><button onClick={onClose} className={btnSecondary} disabled={busy}>Cancel</button><button onClick={go} disabled={busy} className={btnPrimary}>{busy ? 'Publishing…' : 'Make it live'}</button></>}
    >
      <div className="space-y-3">
        <div className="text-[10px] text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">{doc.title}</div>
        <p className="text-[10px] text-gray-600">
          Version {version.version_number} becomes the version staff see. {doc.version_number ? `Version ${doc.version_number} is superseded and kept in the history.` : ''}
          {version.change_summary ? ` Change summary: “${version.change_summary}”` : ''}
        </p>
        {needsAck ? (
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={reset} onChange={(e) => setReset(e.target.checked)} className="mt-0.5 rounded" />
              <span className="text-[10px] text-amber-900">
                <span className="font-semibold">Everyone must re-acknowledge this version.</span> Existing acknowledgements are cleared and the compliance matrix shows the document outstanding for all staff until they read and confirm again.
              </span>
            </label>
            {!reset && (
              <div className="text-[9px] text-amber-800 pl-6">
                Existing acknowledgements are kept and count for this version. Use this for a correction that does not change what staff are agreeing to (a typo, a name, a date).
              </div>
            )}
          </div>
        ) : (
          <div className="text-[9px] bg-gray-50 border border-gray-200 text-gray-500 px-3 py-2 rounded-lg">
            This document does not require acknowledgement, so nobody is asked to re-read it.
          </div>
        )}
      </div>
    </Modal>
  );
}

// A read-only look at any HTML version (drafts included) for approvers.
function PreviewModal({ doc, version, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[220] bg-black/60 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-4xl h-[92vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        <div className="bg-navy px-5 py-3 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] text-white/60 font-mono">{doc.doc_number} · v{version.version_number} · {version.status.replace('_', ' ')}</div>
            <div className="font-bold text-[14px] text-white truncate">{doc.title}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-1 rounded">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <HtmlDocumentView doc={doc} versionId={version.id} />
      </div>
    </div>
  );
}

const CATEGORIES = [
  { value: 'quality',       label: 'Quality' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'security',      label: 'Security' },
  { value: 'general',       label: 'General' },
];

const ACCREDITATION_TYPES = [
  { value: 'iso_9001',              label: 'ISO 9001 — Quality Management' },
  { value: 'iso_27001',             label: 'ISO 27001 — Information Security' },
  { value: 'iso_14001',             label: 'ISO 14001 — Environmental' },
  { value: 'constructionline_gold', label: 'Constructionline Gold' },
  { value: 'safecontractor',        label: 'SafeContractor' },
  { value: 'other',                 label: 'Other' },
];

const KNOWN_ACCREDITATION_TYPES = [
  { type: 'iso_9001',              label: 'ISO 9001',           sub: 'Quality Management' },
  { type: 'iso_27001',             label: 'ISO 27001',          sub: 'Information Security' },
  { type: 'iso_14001',             label: 'ISO 14001',          sub: 'Environmental' },
  { type: 'constructionline_gold', label: 'Constructionline',   sub: 'Gold' },
  { type: 'safecontractor',        label: 'SafeContractor',     sub: 'H&S Accreditation' },
];

const DOC_STATUS_LABELS = {
  draft:   'Draft',
  live:    'Live',
  archived: 'Archived',
};

const VERSION_STATUS_COLORS = {
  draft:            'bg-gray-100 text-gray-600',
  pending_approval: 'bg-amber-100 text-amber-700',
  live:             'bg-emerald-100 text-emerald-700',
  superseded:       'bg-gray-100 text-gray-500',
};

function accStatusColors(status) {
  if (status === 'expired')  return { dot: 'bg-red-500',     badge: 'bg-red-100 text-red-700' };
  if (status === 'due_soon') return { dot: 'bg-amber-400',   badge: 'bg-amber-100 text-amber-700' };
  return                            { dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700' };
}

const AUDIT_OUTCOMES = [
  { value: 'passed',                   label: 'Passed',               cls: 'bg-emerald-100 text-emerald-700' },
  { value: 'passed_with_observations', label: 'Passed w/ Observations', cls: 'bg-amber-100 text-amber-700' },
  { value: 'minor_nc',                 label: 'Minor NC',             cls: 'bg-orange-100 text-orange-700' },
  { value: 'major_nc',                 label: 'Major NC',             cls: 'bg-red-100 text-red-700' },
  { value: 'surveillance',             label: 'Surveillance',         cls: 'bg-blue-100 text-blue-700' },
  { value: 'certification',            label: 'Certification',        cls: 'bg-indigo-100 text-indigo-700' },
  { value: 'recertification',          label: 'Recertification',      cls: 'bg-indigo-100 text-indigo-700' },
];
function auditOutcomeMeta(value) {
  return AUDIT_OUTCOMES.find(o => o.value === value) || { label: value || '—', cls: 'bg-gray-100 text-gray-500' };
}

function useActiveEmployees() {
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    api.get('people').then(data => {
      const list = (Array.isArray(data) ? data : data?.results ?? [])
        .filter(p => p.employee_status !== 'left' && p.employee_status !== 'archived')
        .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
      setEmployees(list);
    }).catch(() => {});
  }, []);
  return employees;
}

function OwnerSelect({ value, onChange, className }) {
  const employees = useActiveEmployees();
  return (
    <select className={className} value={value} onChange={e => onChange(e.target.value)}>
      <option value="">— Select owner —</option>
      {employees.map(p => {
        const name = `${p.first_name} ${p.last_name}`.trim();
        return <option key={p.id} value={name}>{name}</option>;
      })}
      {value && !employees.some(p => `${p.first_name} ${p.last_name}`.trim() === value) && (
        <option value={value}>{value}</option>
      )}
    </select>
  );
}

// ── Shared form field helper ──────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[9px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = 'w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-navy/30';
const btnPrimary = 'px-3 py-1.5 bg-navy text-white text-[11px] font-semibold rounded-lg hover:bg-navy/80 disabled:opacity-50 transition-colors';
const btnSecondary = 'px-3 py-1.5 border border-gray-200 text-[11px] text-gray-600 rounded-lg hover:border-gray-300 transition-colors';
const btnDanger = 'px-3 py-1.5 bg-red-500 text-white text-[11px] font-semibold rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors';
const btnGold = 'px-3 py-1.5 bg-navy text-white text-[11px] font-semibold rounded-lg hover:bg-navy/80 disabled:opacity-50 transition-colors';

// ── Doc Version History Panel ─────────────────────────────────────────────────
function DocHistoryPanel({ docId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null); // { vId, type: 'pdf'|'word' }
  // A refused replacement says why beside the version it was for, not in a toast.
  const [replaceError, setReplaceError] = useState(null); // { vId, message }
  const [replacing, setReplacing] = useState(false);
  const [goLiveVersion, setGoLiveVersion] = useState(null);
  const [previewVersion, setPreviewVersion] = useState(null);
  const [editing, setEditing] = useState(null); // { version, content }
  const [discarding, setDiscarding] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    api.get(`qms/documents/${docId}`)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [docId]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleResetReads = async () => {
    if (!confirm(`Reset ALL acknowledgements for "${data?.title}"?\n\nAll staff will need to re-read and re-acknowledge this document. This cannot be undone.`)) return;
    setResetting(true);
    try {
      const res = await api.delete(`qms/documents/${docId}/reads`);
      toast(`Acknowledgements reset (${res.deleted ?? 0} cleared)`, 'success');
      load();
    } catch (e) {
      toast(e.message || 'Failed to reset acknowledgements', 'error');
    } finally {
      setResetting(false);
    }
  };

  const triggerReplace = (vId, type) => {
    setReplaceTarget({ vId, type });
    setReplaceError(null);
    fileInputRef.current.value = '';
    fileInputRef.current.accept = acceptAttribute(type);
    fileInputRef.current.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !replaceTarget) return;
    setReplacing(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const base64 = btoa(binary);
      const payload = replaceTarget.type === 'pdf'
        ? { filename: file.name, content_type: file.type, data: base64, file_size: file.size }
        : { word_filename: file.name, word_content_type: file.type, word_data: base64, word_file_size: file.size };
      await api.patch(`qms/documents/${docId}/versions/${replaceTarget.vId}/files`, payload);
      toast(`File replaced: ${file.name}`, 'success');
      load();
    } catch (err) {
      setReplaceError({ vId: replaceTarget.vId, message: err.message || 'Upload failed' });
    } finally {
      setReplacing(false);
      setReplaceTarget(null);
    }
  };

  const handleEdit = async (v) => {
    try {
      const content = await api.get(`qms/documents/${docId}/versions/${v.id}/content`);
      setEditing({ version: v, content });
    } catch (e) { toast(e.message || 'Could not open the draft', 'error'); }
  };

  const handleDiscard = async (v) => {
    if (!confirm(`Discard draft v${v.version_number}? Everything written in it is deleted. The live version is not affected.`)) return;
    setDiscarding(true);
    try {
      await api.delete(`qms/documents/${docId}/versions/${v.id}`);
      toast('Draft discarded', 'success');
      load();
    } catch (e) { toast(e.message || 'Could not discard', 'error'); }
    finally { setDiscarding(false); }
  };

  const versions = data?.versions || [];
  const reads = data?.reads || [];

  const readsByVersion = {};
  for (const r of reads) {
    if (!readsByVersion[r.version_id]) readsByVersion[r.version_id] = [];
    readsByVersion[r.version_id].push(r);
  }

  const totalUniqueReaders = new Set(reads.map(r => r.person_id)).size;

  return (
    <div className="fixed inset-0 z-[200] bg-black/50 flex items-start justify-end" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      {/* Hidden file input for replace operations */}
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelected} />

      <div className="bg-white w-full max-w-xl h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="bg-navy px-5 py-4 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-mono text-[11px] text-white/60">{data?.doc_number || '…'}</div>
              <div className="font-bold text-[14px] text-white leading-tight mt-0.5">{data?.title || 'Loading…'}</div>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white p-1 rounded flex-shrink-0">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
          {!loading && (
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3 text-[10px] text-white/50">
                <span>{totalUniqueReaders} unique reader{totalUniqueReaders !== 1 ? 's' : ''}</span>
                <span>·</span>
                <span>{reads.length} total acknowledgement{reads.length !== 1 ? 's' : ''}</span>
                <span>·</span>
                <span>{versions.length} version{versions.length !== 1 ? 's' : ''}</span>
              </div>
              {reads.length > 0 && (
                <button
                  onClick={handleResetReads}
                  disabled={resetting}
                  title="Force all staff to re-read and re-acknowledge"
                  className="text-[9px] font-semibold px-2 py-0.5 rounded bg-red-500/20 text-red-200 hover:bg-red-500/40 transition-colors disabled:opacity-50"
                >
                  {resetting ? 'Resetting…' : '↺ Reset Acks'}
                </button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center flex-1 text-[11px] text-gray-400">Loading…</div>
        ) : versions.length === 0 ? (
          <div className="flex items-center justify-center flex-1 text-[11px] text-gray-400">No versions yet.</div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {versions.map((v, idx) => {
              const vReads = readsByVersion[v.id] || [];
              const isCurrent = v.id === data?.current_version_id;
              return (
                <div key={v.id} className={`border rounded-xl overflow-hidden ${isCurrent ? 'border-navy/30' : 'border-gray-200'}`}>
                  {/* Version header */}
                  <div className={`px-4 py-3 flex items-center justify-between gap-2 ${isCurrent ? 'bg-navy/5' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${VERSION_STATUS_COLORS[v.status] || 'bg-gray-100 text-gray-600'}`}>
                        v{v.version_number}
                      </span>
                      {isCurrent && <span className="text-[8px] font-bold text-navy bg-navy/10 px-1.5 py-0.5 rounded">Current</span>}
                      <span className={`text-[8px] font-semibold uppercase ${VERSION_STATUS_COLORS[v.status] || 'text-gray-500'}`}>{v.status.replace('_', ' ')}</span>
                      <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded ${v.content_kind === 'html' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`} title={v.content_kind === 'html' ? 'Content edited in the app' : 'Uploaded PDF and Word files'}>
                        {CONTENT_KIND_LABEL[v.content_kind] || v.content_kind}
                      </span>
                      {v.content_kind === 'html' && !!v.has_overview && <span className="text-[8px] text-blue-700">+ overview</span>}
                    </div>
                    <div className="flex items-center gap-1.5 text-[9px] text-gray-400 flex-wrap justify-end">
                      <span className="font-semibold text-emerald-600">{vReads.length} ack{vReads.length !== 1 ? 's' : ''}</span>
                      {v.content_kind === 'html' && (
                        <button onClick={() => setPreviewVersion(v)} className="text-[8px] text-navy border border-gray-200 hover:border-navy px-2 py-0.5 rounded font-semibold transition-colors">
                          Preview
                        </button>
                      )}
                      {v.status === 'draft' && v.content_kind === 'html' && (
                        <button onClick={() => handleEdit(v)} className="text-[8px] bg-navy text-white px-2 py-0.5 rounded font-semibold hover:bg-navy/80 transition-colors">
                          Edit
                        </button>
                      )}
                      {v.status === 'draft' && (
                        <button onClick={() => handleDiscard(v)} disabled={discarding} className="text-[8px] text-red-600 border border-red-200 hover:bg-red-50 px-2 py-0.5 rounded font-semibold transition-colors disabled:opacity-50">
                          Discard
                        </button>
                      )}
                      {(v.status === 'draft' || v.status === 'pending_approval') && (
                        <button
                          onClick={() => setGoLiveVersion(v)}
                          className="text-[8px] bg-emerald-500 hover:bg-emerald-600 text-white px-2 py-0.5 rounded font-semibold transition-colors"
                        >
                          Go Live
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Version details */}
                  <div className="px-4 py-3 space-y-2">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[9px]">
                      {v.submitted_by_name && (
                        <><span className="text-gray-400 font-semibold">Submitted by</span><span className="text-gray-700">{v.submitted_by_name}</span></>
                      )}
                      {v.submitted_at && (
                        <><span className="text-gray-400 font-semibold">Submitted</span><span className="text-gray-700">{formatDate(v.submitted_at)}</span></>
                      )}
                      {v.approved_by_name && (
                        <><span className="text-gray-400 font-semibold">Approved by</span><span className="text-gray-700">{v.approved_by_name}</span></>
                      )}
                      {v.live_at && (
                        <><span className="text-gray-400 font-semibold">Published</span><span className="text-gray-700">{formatDate(v.live_at)}</span></>
                      )}
                      {v.content_updated_by_name && (
                        <><span className="text-gray-400 font-semibold">Content last saved by</span><span className="text-gray-700">{v.content_updated_by_name}{v.content_updated_at ? `, ${formatDate(v.content_updated_at)}` : ''}</span></>
                      )}
                      {v.status === 'live' && v.content_kind === 'html' && (
                        <><span className="text-gray-400 font-semibold">Acknowledgements on go-live</span><span className="text-gray-700">{v.reset_acknowledgements === 0 ? 'Kept (minor correction)' : 'Reset'}</span></>
                      )}
                    </div>

                    {v.change_summary && (
                      <div className="text-[10px] text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                        {v.change_summary}
                      </div>
                    )}

                    {replaceError?.vId === v.id && (
                      <div role="alert" className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{replaceError.message}</div>
                    )}
                    {/* Files */}
                    <div className="flex flex-wrap gap-2 pt-1">
                      {v.content_kind === 'html' ? (
                        v.word_r2_key ? (
                          <a
                            href={`/api/uploads/${v.word_r2_key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[9px] text-blue-600 hover:text-blue-700 font-semibold bg-blue-50 border border-blue-200 rounded px-2 py-0.5"
                            title="The Word file this version was imported from"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            Imported from {v.word_filename || 'Word'}
                          </a>
                        ) : (
                          <span className="text-[9px] text-gray-400 italic">Written in the app{v.based_on_version_id ? ', edited from the previous version' : ''}.</span>
                        )
                      ) : v.r2_key ? (
                        <div className="flex items-center gap-1.5">
                          <a
                            href={`/api/uploads/${v.r2_key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[9px] text-red-600 hover:text-red-700 font-semibold bg-red-50 border border-red-200 rounded px-2 py-0.5"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            {v.filename || 'PDF'}
                          </a>
                          <button
                            onClick={() => triggerReplace(v.id, 'pdf')}
                            disabled={replacing}
                            title="Replace PDF"
                            className="text-[8px] text-gray-400 hover:text-navy border border-gray-200 rounded px-1.5 py-0.5 transition-colors"
                          >
                            Replace
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => triggerReplace(v.id, 'pdf')}
                          disabled={replacing}
                          className="inline-flex items-center gap-1 text-[9px] text-gray-400 hover:text-navy border border-dashed border-gray-300 rounded px-2 py-0.5 transition-colors"
                        >
                          + Attach PDF
                        </button>
                      )}

                      {v.content_kind !== 'html' && (v.word_r2_key ? (
                        <div className="flex items-center gap-1.5">
                          <a
                            href={`/api/uploads/${v.word_r2_key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[9px] text-blue-600 hover:text-blue-700 font-semibold bg-blue-50 border border-blue-200 rounded px-2 py-0.5"
                          >
                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                            {v.word_filename || 'Word'}
                          </a>
                          <button
                            onClick={() => triggerReplace(v.id, 'word')}
                            disabled={replacing}
                            title="Replace Word file"
                            className="text-[8px] text-gray-400 hover:text-navy border border-gray-200 rounded px-1.5 py-0.5 transition-colors"
                          >
                            Replace
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => triggerReplace(v.id, 'word')}
                          disabled={replacing}
                          className="inline-flex items-center gap-1 text-[9px] text-gray-400 hover:text-navy border border-dashed border-gray-300 rounded px-2 py-0.5 transition-colors"
                        >
                          + Attach Word
                        </button>
                      ))}
                    </div>

                    {/* Acknowledgements for this version */}
                    {vReads.length > 0 && (
                      <div>
                        <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Acknowledged by</div>
                        <div className="space-y-1 max-h-48 overflow-y-auto">
                          {vReads.map((r, i) => (
                            <div key={i} className="flex items-center justify-between text-[9px] py-1 border-b border-gray-50 last:border-0">
                              <span className="font-medium text-gray-700">{r.person_name}</span>
                              <span className="text-gray-400">{formatDate(r.read_at)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {vReads.length === 0 && v.status !== 'draft' && v.status !== 'pending_approval' && (
                      <div className="text-[9px] text-gray-400 italic">No acknowledgements recorded for this version.</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {goLiveVersion && data && (
        <GoLiveModal doc={data} version={goLiveVersion} onClose={() => setGoLiveVersion(null)} onDone={() => { setGoLiveVersion(null); load(); }} />
      )}
      {previewVersion && data && (
        <PreviewModal doc={data} version={previewVersion} onClose={() => setPreviewVersion(null)} />
      )}
      {editing && data && (
        <PolicyEditor doc={data} version={editing.version} initialContent={editing.content} onClose={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

// ── Tab 1: Documents ──────────────────────────────────────────────────────────
function DocumentsTab({ onDocumentsChanged }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editDoc, setEditDoc] = useState(null);
  const [uploadDoc, setUploadDoc] = useState(null);
  const [historyDocId, setHistoryDocId] = useState(null);
  const [editing, setEditing] = useState(null); // { doc, version, content }
  const [goLive, setGoLive] = useState(null);   // { doc, version }
  const [opening, setOpening] = useState(null); // doc id whose draft is being created
  const [ackDoc, setAckDoc] = useState(null);   // document whose acknowledgement register is open
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // documents ticked for deletion
  const [deleting, setDeleting] = useState(null); // documents the delete dialog is open on
  const { toast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    setSelectedIds(new Set());
    api.get('qms/documents').then(setDocs).catch(() => toast('Failed to load documents', 'error')).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = statusFilter === 'all' ? docs : docs.filter(d => {
    if (statusFilter === 'draft') return !!d.pending_version_id;
    return d.status === statusFilter;
  });

  // Ticking is scoped to what is on screen: "select all" takes the filtered
  // list, so a filter is the way to aim a clear-out rather than a second control.
  const selectedDocs = docs.filter(d => selectedIds.has(d.id));
  const allShownSelected = filtered.length > 0 && filtered.every(d => selectedIds.has(d.id));

  const toggleOne = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleAllShown = () => setSelectedIds(prev => {
    const next = new Set(prev);
    if (allShownSelected) filtered.forEach(d => next.delete(d.id));
    else filtered.forEach(d => next.add(d.id));
    return next;
  });

  // Edit content: the live version is copied into a draft (or the open draft is
  // re-opened) and the editor opens on it.
  const startEditing = async (doc, { quiet = false } = {}) => {
    setOpening(doc.id);
    try {
      const { version, content } = await openDraftForEditing(doc.id);
      // After an import the draft was created a moment ago, so "re-opened" would mislead.
      if (version.reopened && !quiet) toast(`Re-opened draft v${version.version_number}`, 'info');
      setEditing({ doc, version, content });
    } catch (e) {
      toast(e.message || 'Could not open the draft', 'error');
    } finally {
      setOpening(null);
    }
  };

  const handleArchive = async (doc) => {
    if (!confirm(`Archive "${doc.title}"?`)) return;
    try {
      await api.put(`qms/documents/${doc.id}`, { status: 'archived' });
      toast('Document archived', 'success');
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleApprove = async (doc) => {
    if (!doc.pending_version_id) { toast('No pending version to approve', 'error'); return; }
    if (!confirm(`Approve and publish version ${doc.pending_version_number}?`)) return;
    try {
      await api.put(`qms/documents/${doc.id}/versions/${doc.pending_version_id}/approve`, {});
      toast('Version approved and published', 'success');
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleGoLive = (doc) => {
    if (!doc.pending_version_id) { toast('No draft version to publish', 'error'); return; }
    setGoLive({ doc, version: { id: doc.pending_version_id, version_number: doc.pending_version_number, change_summary: doc.pending_change_summary } });
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1">
          {['all', 'draft', 'live', 'archived'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-[9px] px-2.5 py-1 rounded-full font-semibold transition-colors ${
                statusFilter === s ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s === 'all' ? 'All' : s === 'draft' ? 'Draft' : DOC_STATUS_LABELS[s] || s}
            </button>
          ))}
        </div>
        <button onClick={() => setShowCreate(true)} className={btnPrimary} data-tour="qms-new-document">
          + New Document
        </button>
      </div>

      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-2 rounded-lg bg-red-50 border border-red-100">
          <span className="text-[11px] font-semibold text-red-700">
            {selectedIds.size} document{selectedIds.size === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds(new Set())} className="text-[10px] text-gray-500 hover:text-gray-700">
              Clear selection
            </button>
            <button onClick={() => setDeleting(selectedDocs)} className={btnDanger} data-tour="qms-delete-selected">
              Delete selected
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[11px] text-gray-400 py-8 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-[11px] text-gray-400 py-8 text-center">No documents found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-2 w-6">
                  <input
                    type="checkbox"
                    checked={allShownSelected}
                    onChange={toggleAllShown}
                    aria-label="Select every document shown"
                    className="w-3 h-3 rounded border-gray-300 accent-navy cursor-pointer"
                  />
                </th>
                {['Doc #', 'Title', 'Category', 'Type', 'Version', 'Status', 'Review Due', 'Owner', 'Actions'].map(h => (
                  <th key={h} className="text-left py-2 pr-3 text-[8px] font-bold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(doc => {
                const hasPending = !!doc.pending_version_id;
                return (
                  <tr key={doc.id} className={`border-b border-gray-50 hover:bg-gray-50/50 ${selectedIds.has(doc.id) ? 'bg-red-50/40' : ''}`}>
                    <td className="py-2 pr-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(doc.id)}
                        onChange={() => toggleOne(doc.id)}
                        aria-label={`Select ${doc.doc_number}`}
                        className="w-3 h-3 rounded border-gray-300 accent-navy cursor-pointer"
                      />
                    </td>
                    <td className="py-2 pr-3 font-mono font-semibold text-navy">{doc.doc_number}</td>
                    <td className="py-2 pr-3 font-semibold text-navy max-w-[180px] truncate">{doc.title}</td>
                    <td className="py-2 pr-3 capitalize text-gray-600">{doc.category}</td>
                    <td className="py-2 pr-3 text-gray-600">{docTypeLabel(doc.doc_type)}</td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-col gap-0.5">
                        {doc.version_number && (
                          <div className="flex items-center gap-1">
                            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${VERSION_STATUS_COLORS[doc.version_status] || 'bg-gray-100 text-gray-600'}`}>
                              v{doc.version_number}
                            </span>
                            {doc.word_r2_key && (
                              <a href={`/api/uploads/${doc.word_r2_key}`} target="_blank" rel="noopener noreferrer" title="Download Word source" className="text-[7px] text-blue-500 hover:underline">docx</a>
                            )}
                          </div>
                        )}
                        {hasPending && (
                          <div className="flex items-center gap-1">
                            <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                              v{doc.pending_version_number} {doc.pending_status === 'pending_approval' ? 'awaiting approval' : 'draft'}
                            </span>
                            {doc.pending_word_r2_key && (
                              <a href={`/api/uploads/${doc.pending_word_r2_key}`} target="_blank" rel="noopener noreferrer" title="Download pending Word source" className="text-[7px] text-blue-500 hover:underline">docx</a>
                            )}
                          </div>
                        )}
                        {!doc.version_number && !hasPending && <span className="text-gray-300">—</span>}
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase ${
                        doc.status === 'live' ? 'bg-emerald-100 text-emerald-700' :
                        doc.status === 'archived' ? 'bg-gray-100 text-gray-500' :
                        'bg-gray-100 text-gray-600'
                      }`}>{DOC_STATUS_LABELS[doc.status] || doc.status}</span>
                    </td>
                    <td className="py-2 pr-3 text-gray-500">{doc.next_review_date ? formatDate(doc.next_review_date) : '—'}</td>
                    <td className="py-2 pr-3 text-gray-500 max-w-[100px] truncate">{doc.owner_name || '—'}</td>
                    <td className="py-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {/* Who has and has not acknowledged: only meaningful where acknowledgement is required */}
                        {!!doc.requires_acknowledgement && (
                          <button
                            onClick={() => setAckDoc(doc)}
                            title="Who has and has not acknowledged this document"
                            className="p-1 text-gray-400 hover:text-gold rounded transition-colors"
                            data-tour="qms-acknowledgements"
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                              <polyline points="16 11 18 13 22 9"/>
                            </svg>
                          </button>
                        )}
                        {/* Version history */}
                        <button
                          onClick={() => setHistoryDocId(doc.id)}
                          title="Version history & acknowledgements"
                          className="p-1 text-gray-400 hover:text-gold rounded transition-colors"
                          data-tour="qms-history"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                          </svg>
                        </button>
                        {/* Edit */}
                        <button
                          onClick={() => setEditDoc(doc)}
                          title="Edit metadata"
                          className="p-1 text-gray-400 hover:text-navy rounded transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </button>
                        {/* Edit content in the app */}
                        <button
                          onClick={() => startEditing(doc)}
                          disabled={opening === doc.id || doc.pending_status === 'pending_approval'}
                          title={doc.pending_status === 'pending_approval' ? 'A version is awaiting approval; approve or discard it first' : doc.pending_version_id ? 'Continue editing the open draft' : 'Edit the content in the app (creates a new draft version)'}
                          data-tour="qms-edit-content"
                          className="text-[8px] font-semibold px-2 py-0.5 rounded border border-navy/30 text-navy hover:bg-navy hover:text-white transition-colors disabled:opacity-40"
                        >
                          {opening === doc.id ? 'Opening…' : doc.pending_version_id && doc.pending_content_kind === 'html' ? 'Continue draft' : 'Edit content'}
                        </button>
                        {/* Upload version */}
                        <button
                          onClick={() => setUploadDoc(doc)}
                          title="New version from files or a Word import"
                          className="p-1 text-gray-400 hover:text-navy rounded transition-colors"
                          data-tour="qms-new-version"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                        </button>
                        {/* Go Live */}
                        {hasPending && (
                          <button
                            onClick={() => handleGoLive(doc)}
                            className="text-[8px] bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded font-semibold transition-colors"
                            data-tour="qms-go-live"
                          >
                            Go Live
                          </button>
                        )}
                        {/* Archive */}
                        {doc.status === 'live' && (
                          <button
                            onClick={() => handleArchive(doc)}
                            title="Archive document"
                            className="p-1 text-gray-300 hover:text-red-400 rounded transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5" rx="1"/>
                              <line x1="10" y1="12" x2="14" y2="12"/>
                            </svg>
                          </button>
                        )}
                        {/* Delete: archiving keeps the history, this does not */}
                        <button
                          onClick={() => setDeleting([doc])}
                          title="Delete document permanently"
                          className="p-1 text-gray-300 hover:text-red-600 rounded transition-colors"
                          data-tour="qms-delete"
                        >
                          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && <CreateDocModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editDoc && <EditDocModal doc={editDoc} onClose={() => setEditDoc(null)} onSaved={() => { setEditDoc(null); load(); }} />}
      {uploadDoc && (
        <UploadVersionModal
          doc={uploadDoc}
          onClose={() => setUploadDoc(null)}
          onSaved={() => { setUploadDoc(null); load(); }}
          onStartEditing={() => { const d = uploadDoc; setUploadDoc(null); startEditing(d, { quiet: true }); }}
        />
      )}
      {historyDocId && <DocHistoryPanel docId={historyDocId} onClose={() => { setHistoryDocId(null); load(); }} />}
      {ackDoc && <AcknowledgementsModal doc={ackDoc} onClose={() => setAckDoc(null)} />}
      {editing && (
        <PolicyEditor
          doc={editing.doc}
          version={editing.version}
          initialContent={editing.content}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
      {goLive && (
        <GoLiveModal doc={goLive.doc} version={goLive.version} onClose={() => setGoLive(null)} onDone={() => { setGoLive(null); load(); }} />
      )}
      {deleting && (
        <DeleteDocumentsModal
          docs={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={(count) => {
            setDeleting(null);
            toast(`${count} document${count === 1 ? '' : 's'} deleted`, 'success');
            load();
            onDocumentsChanged?.();
          }}
        />
      )}
    </div>
  );
}

// Deleting is the one thing on this page that cannot be undone, so the dialog
// says exactly what goes and asks for the word to be typed. Archiving is
// offered in the same breath, because it is what most people actually want.
function DeleteDocumentsModal({ docs, onClose, onDeleted }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const many = docs.length > 1;
  const confirmed = typed.trim().toUpperCase() === 'DELETE';

  const handleDelete = async () => {
    if (!confirmed) { setError('Type DELETE to confirm.'); return; }
    setBusy(true);
    setError('');
    try {
      if (many) await api.post('qms/documents/bulk-delete', { ids: docs.map(d => d.id) });
      else await api.delete(`qms/documents/${docs[0].id}`);
      onDeleted(docs.length);
    } catch (e) {
      setError(e.message || 'Could not delete. Nothing has changed.');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={many ? `Delete ${docs.length} documents` : 'Delete document'}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className={btnSecondary} disabled={busy}>Keep them</button>
          <button onClick={handleDelete} className={btnDanger} disabled={busy || !confirmed}>
            {busy ? 'Deleting…' : many ? `Delete ${docs.length} documents` : 'Delete document'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-[11px] text-gray-600">
          This permanently removes {many ? 'these documents' : 'this document'} and everything held against
          {many ? ' them' : ' it'}: every version, every acknowledgement record of who has read
          {many ? ' them' : ' it'}, and the uploaded files. It cannot be undone.
        </p>
        <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
          {docs.map(d => (
            <div key={d.id} className="flex items-center gap-2 px-2.5 py-1.5">
              <span className="font-mono text-[10px] font-semibold text-navy shrink-0">{d.doc_number}</span>
              <span className="text-[10px] text-gray-600 truncate">{d.title}</span>
              {d.status === 'live' && (
                <span className="ml-auto text-[8px] font-bold px-1.5 py-0.5 rounded uppercase bg-emerald-100 text-emerald-700 shrink-0">Live</span>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-gray-500">
          To take {many ? 'documents' : 'a document'} out of the library but keep the history, archive
          {many ? ' them' : ' it'} instead.
        </p>
        <div>
          <label className="block text-[10px] font-semibold text-gray-600 mb-1">Type DELETE to confirm</label>
          <input
            type="text"
            value={typed}
            onChange={e => { setTyped(e.target.value); if (error) setError(''); }}
            autoFocus
            className="w-full px-2.5 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-navy"
            placeholder="DELETE"
          />
          {error && <p className="mt-1 text-[10px] font-semibold text-red-600">{error}</p>}
        </div>
      </div>
    </Modal>
  );
}

// Who has and who has not acknowledged the live version of one document, by
// name. The compliance matrix answers this for every document at once; this is
// the same question for the one document in front of you, opened from its row.
// Read-only: chasing somebody is a conversation, and the Action raised when the
// version went live is already sitting in their queue.
function AcknowledgementsModal({ doc, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('outstanding');

  useEffect(() => {
    setData(null); setError('');
    api.get(`qms/documents/${doc.id}/acknowledgements`)
      .then(d => { setData(d); if (d.outstanding.length === 0 && d.acknowledged.length > 0) setTab('acknowledged'); })
      .catch(e => setError(e.message || 'Could not load the acknowledgements'));
  }, [doc.id]);

  const required = data ? data.acknowledged.length + data.outstanding.length : 0;
  const pct = required ? Math.round((data.acknowledged.length / required) * 100) : 0;
  const tabs = data ? [
    { key: 'outstanding',  label: 'Not yet acknowledged', n: data.outstanding.length,  rows: data.outstanding },
    { key: 'acknowledged', label: 'Acknowledged',         n: data.acknowledged.length, rows: data.acknowledged },
    { key: 'exempt',       label: 'Exempt',               n: data.exempt.length,       rows: data.exempt },
  ] : [];
  const current = tabs.find(t => t.key === tab);

  return (
    <Modal title={`Acknowledgements — ${doc.doc_number}`} onClose={onClose} wide
      footer={<button onClick={onClose} className={btnSecondary}>Close</button>}
    >
      <div className="space-y-3">
        <div className="text-[10px] text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
          <div className="font-semibold text-navy">{doc.title}</div>
          {data?.document?.version_number && (
            <div className="mt-0.5">Version {data.document.version_number}{data.document.live_at ? `, live since ${formatDate(data.document.live_at)}` : ''}</div>
          )}
        </div>

        {error && <div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        {!data && !error && <div className="text-[11px] text-gray-400 py-6 text-center">Loading…</div>}

        {data && (
          <>
            <div>
              <div className="flex items-center justify-between text-[10px] mb-1">
                <span className="font-semibold text-navy">{data.acknowledged.length} of {required} acknowledged</span>
                <span className="text-gray-400">{pct}%</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${pct === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
              </div>
              {!data.open_action && data.outstanding.length > 0 && (
                <div className="text-[9px] text-gray-400 mt-1">No open Action is asking for this document; the acknowledgements pre-date that, or the document is not live. Going live with a new version raises one.</div>
              )}
            </div>

            <div className="flex gap-1">
              {tabs.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`text-[9px] px-2.5 py-1 rounded-full font-semibold transition-colors ${tab === t.key ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {t.label} ({t.n})
                </button>
              ))}
            </div>

            {current.rows.length === 0 ? (
              <div className="text-[10px] text-gray-400 py-4 text-center">
                {tab === 'outstanding' ? 'Everyone who needs to has acknowledged this document.' : 'Nobody here.'}
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg max-h-[50vh] overflow-y-auto">
                {current.rows.map(r => (
                  <li key={r.person_id} className="flex items-center justify-between gap-3 px-3 py-1.5 text-[10px]">
                    <span className="text-navy font-medium">{r.name}</span>
                    {r.read_at
                      ? <span className="text-emerald-700">Acknowledged {formatDate(r.read_at)}</span>
                      : tab === 'exempt'
                        ? <span className="text-gray-400">Exempt from acknowledgements</span>
                        : <span className="text-amber-700 font-semibold">Outstanding</span>}
                  </li>
                ))}
              </ul>
            )}

            {tab === 'acknowledged' && data.left.length > 0 && (
              <div className="text-[9px] text-gray-400">
                Also acknowledged by {data.left.length} {data.left.length === 1 ? 'person who has' : 'people who have'} since left: {data.left.map(r => r.name).join(', ')}.
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function CreateDocModal({ onClose, onSaved }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    doc_number: '', title: '', description: '', category: 'quality', doc_type: 'procedure',
    requires_acknowledgement: false, review_period_months: 12, owner_name: '', sort_order: 0, version_number: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.doc_number || !form.title) { toast('Doc number and title are required', 'error'); return; }
    setSaving(true);
    try {
      await api.post('qms/documents', form);
      toast('Document created', 'success');
      onSaved();
    } catch (e) { toast(e.message, 'error'); } finally { setSaving(false); }
  };

  return (
    <Modal title="New Document" onClose={onClose} wide
      footer={<><button onClick={onClose} className={btnSecondary}>Cancel</button><button onClick={handleSave} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Create'}</button></>}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Doc Number" required><input className={inputCls} value={form.doc_number} onChange={e => set('doc_number', e.target.value)} placeholder="AV-POL-001" /></Field>
          <Field label="Owner"><OwnerSelect className={inputCls} value={form.owner_name} onChange={v => set('owner_name', v)} /></Field>
        </div>
        <Field label="Title" required><input className={inputCls} value={form.title} onChange={e => set('title', e.target.value)} placeholder="Document title" /></Field>
        <Field label="Description"><textarea className={inputCls} rows={2} value={form.description} onChange={e => set('description', e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select className={inputCls} value={form.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Doc Type">
            <select className={inputCls} value={form.doc_type} onChange={e => set('doc_type', e.target.value)}>
              {QMS_DOC_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Review Period">
            <select className={inputCls} value={form.review_period_months} onChange={e => set('review_period_months', Number(e.target.value))}>
              <option value={6}>6 months</option>
              <option value={12}>12 months</option>
              <option value={24}>24 months</option>
            </select>
          </Field>
          <Field label="Initial Version (optional)">
            <input className={inputCls} value={form.version_number} onChange={e => set('version_number', e.target.value)} placeholder="e.g. 1.0" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Sort Order"><input type="number" className={inputCls} value={form.sort_order} onChange={e => set('sort_order', Number(e.target.value))} /></Field>
          <div className="flex items-center gap-2 pt-5">
            <input type="checkbox" id="req-ack" checked={form.requires_acknowledgement} onChange={e => set('requires_acknowledgement', e.target.checked)} className="rounded" />
            <label htmlFor="req-ack" className="text-[10px] text-gray-600">Requires acknowledgement</label>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function EditDocModal({ doc, onClose, onSaved }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: doc.title || '', description: doc.description || '',
    category: doc.category || 'quality', doc_type: doc.doc_type || 'procedure',
    requires_acknowledgement: !!doc.requires_acknowledgement,
    review_period_months: doc.review_period_months || 12,
    owner_name: doc.owner_name || '', sort_order: doc.sort_order || 0,
    next_review_date: doc.next_review_date || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [accTypes, setAccTypes] = useState(
    (doc.accreditation_types || '').split(',').filter(Boolean)
  );
  const toggleAcc = (type) => setAccTypes(prev =>
    prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put(`qms/documents/${doc.id}`, form),
        api.post(`qms/documents/${doc.id}/accreditations`, { types: accTypes }),
      ]);
      toast('Document updated', 'success');
      onSaved();
    } catch (e) { toast(e.message, 'error'); } finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit — ${doc.doc_number}`} onClose={onClose} wide
      footer={<><button onClick={onClose} className={btnSecondary}>Cancel</button><button onClick={handleSave} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button></>}
    >
      <div className="space-y-3">
        <Field label="Title" required><input className={inputCls} value={form.title} onChange={e => set('title', e.target.value)} /></Field>
        <Field label="Description"><textarea className={inputCls} rows={2} value={form.description} onChange={e => set('description', e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category">
            <select className={inputCls} value={form.category} onChange={e => set('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Doc Type">
            <select className={inputCls} value={form.doc_type} onChange={e => set('doc_type', e.target.value)}>
              {QMS_DOC_TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Review Period">
            <select className={inputCls} value={form.review_period_months} onChange={e => set('review_period_months', Number(e.target.value))}>
              <option value={6}>6 months</option>
              <option value={12}>12 months</option>
              <option value={24}>24 months</option>
            </select>
          </Field>
          <Field label="Next Review Date">
            <input type="date" className={inputCls} value={form.next_review_date} onChange={e => set('next_review_date', e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Owner"><OwnerSelect className={inputCls} value={form.owner_name} onChange={v => set('owner_name', v)} /></Field>
          <Field label="Sort Order"><input type="number" className={inputCls} value={form.sort_order} onChange={e => set('sort_order', Number(e.target.value))} /></Field>
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="edit-req-ack" checked={form.requires_acknowledgement} onChange={e => set('requires_acknowledgement', e.target.checked)} className="rounded" />
          <label htmlFor="edit-req-ack" className="text-[10px] text-gray-600">Requires acknowledgement</label>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Accreditations / Certifications</label>
          <div className="flex flex-wrap gap-2">
            {KNOWN_ACCREDITATION_TYPES.map(a => (
              <label key={a.type} className="flex items-center gap-1.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={accTypes.includes(a.type)}
                  onChange={() => toggleAcc(a.type)}
                  className="rounded"
                />
                <span className="text-[9px] text-gray-700">{a.label} <span className="text-gray-400">({a.sub})</span></span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// Three ways to start a new version:
//   import  - a Word document, converted in the browser into an HTML draft that
//             opens in the editor (the Word file is kept as the audit copy);
//   blank   - an HTML draft copied from the live version (or empty), straight
//             into the editor;
//   files   - the original PDF-plus-Word upload, for forms and spreadsheets
//             that are not prose.
const VERSION_MODES = [
  { key: 'import', label: 'Import from Word', blurb: 'Convert a .docx into editable content' },
  { key: 'blank',  label: 'Write in the app', blurb: 'Start from the live version, or from nothing' },
  { key: 'files',  label: 'Upload PDF + Word', blurb: 'Keep this version as attached files' },
];

function UploadVersionModal({ doc, onClose, onSaved, onStartEditing }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  // A refusal (a file the server would not take) is said in this dialog, beside the files.
  const [saveError, setSaveError] = useState('');
  const [mode, setMode] = useState(doc.content_kind === 'file' && doc.version_number ? 'files' : 'import');
  const [form, setForm] = useState({ version_number: '', change_summary: '' });
  const [pdfData, setPdfData] = useState(null);
  const [wordData, setWordData] = useState(null);
  const [imported, setImported] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const suggestedVersion = (() => {
    const cur = doc.version_number;
    if (!cur) return '1.0';
    const m = /^v?(\d+)(?:\.(\d+))?$/i.exec(String(cur).trim());
    if (!m) return '';
    return mode === 'files' ? `${parseInt(m[1], 10) + 1}.0` : `${m[1]}.${(m[2] === undefined ? 0 : parseInt(m[2], 10)) + 1}`;
  })();

  const blocked = doc.pending_version_id
    ? `Version ${doc.pending_version_number} is already ${doc.pending_status === 'pending_approval' ? 'awaiting approval' : 'a draft'}. Publish or discard it before starting another.`
    : null;

  const handleSave = async () => {
    if (blocked) { toast(blocked, 'error'); return; }
    const versionNumber = form.version_number || suggestedVersion;
    if (!versionNumber) { toast('Version number is required', 'error'); return; }
    setSaving(true);
    setSaveError('');
    try {
      if (mode === 'files') {
        if (!pdfData || !wordData) { toast('Both PDF and Word documents are required', 'error'); return; }
        await api.post(`qms/documents/${doc.id}/versions`, {
          version_number: versionNumber,
          change_summary: form.change_summary || null,
          filename: pdfData.filename, content_type: pdfData.content_type, data: pdfData.data, file_size: pdfData.file_size,
          word_filename: wordData.filename, word_content_type: wordData.content_type, word_data: wordData.data, word_file_size: wordData.file_size,
        });
        toast('Draft version created. Review the files, then Go Live to publish', 'success');
        onSaved();
      } else if (mode === 'import') {
        if (!imported) { toast('Choose a Word document to import', 'error'); return; }
        await api.post(`qms/documents/${doc.id}/versions`, {
          content_kind: 'html',
          version_number: versionNumber,
          change_summary: form.change_summary || `Imported from Word (${imported.wordData.word_filename})`,
          html_content: imported.html,
          summary_html: '',
          ...imported.wordData,
        });
        toast(`Imported as draft v${versionNumber}. Opening the editor…`, 'success');
        onStartEditing();
      }
    } catch (e) { setSaveError(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  const footer = mode === 'blank'
    ? <><button onClick={onClose} className={btnSecondary}>Cancel</button><button onClick={() => { if (blocked && doc.pending_content_kind !== 'html') { toast(blocked, 'error'); return; } onStartEditing(); }} className={btnPrimary}>{doc.pending_version_id ? 'Continue the open draft' : 'Open the editor'}</button></>
    : <><button onClick={onClose} className={btnSecondary}>Cancel</button><button onClick={handleSave} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : mode === 'import' ? 'Import and edit' : 'Save Draft'}</button></>;

  return (
    <Modal title={`New Version — ${doc.doc_number}`} onClose={onClose} wide footer={footer}>
      <div className="space-y-3">
        <div className="text-[10px] text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">{doc.title}</div>

        <div className="grid grid-cols-3 gap-2" role="tablist" aria-label="How to create the version">
          {VERSION_MODES.map(m => (
            <button
              key={m.key}
              role="tab"
              aria-selected={mode === m.key}
              onClick={() => setMode(m.key)}
              className={`text-left rounded-lg border px-3 py-2 transition-colors ${mode === m.key ? 'border-navy bg-navy/5' : 'border-gray-200 hover:border-navy/40'}`}
            >
              <div className={`text-[10px] font-semibold ${mode === m.key ? 'text-navy' : 'text-gray-700'}`}>{m.label}</div>
              <div className="text-[8px] text-gray-400 mt-0.5">{m.blurb}</div>
            </button>
          ))}
        </div>

        {blocked && (
          <div className="text-[9px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg">{blocked}</div>
        )}

        {mode !== 'blank' && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Version Number" required>
              <input className={inputCls} value={form.version_number} onChange={e => set('version_number', e.target.value)} placeholder={suggestedVersion || 'e.g. 2.0'} />
            </Field>
            <Field label="Change Summary">
              <input className={inputCls} value={form.change_summary} onChange={e => set('change_summary', e.target.value)} placeholder={mode === 'import' ? 'Imported from Word' : 'What changed in this version?'} />
            </Field>
          </div>
        )}

        {mode === 'import' && <WordImport onReady={setImported} />}

        {saveError && <div role="alert" className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{saveError}</div>}

        {mode === 'blank' && (
          <div className="text-[10px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 leading-relaxed">
            {doc.version_number
              ? <>A draft is created from the live version (v{doc.version_number}) with the next version number suggested, and opens in the editor. Nothing changes for staff until it is approved and goes live.</>
              : <>An empty draft (v1.0) is created and opens in the editor.</>}
            {doc.content_kind === 'file' && doc.version_number && <> The live version is an uploaded PDF, so the draft starts empty: import the Word file instead if you want its text.</>}
          </div>
        )}

        {mode === 'files' && (
          <>
            {doc.requires_acknowledgement ? (
              <div className="text-[9px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded-lg">
                This document requires acknowledgement. You choose at go-live whether existing acknowledgements are reset.
              </div>
            ) : null}
            <Field label="PDF — Published version (visible to all staff)" required>
              <FileUploadWithData onFileReady={setPdfData} kind="pdf" />
            </Field>
            <Field label="Word — Source document (admin only)" required>
              <FileUploadWithData onFileReady={setWordData} kind="word" />
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}

// Custom file picker that returns raw file data (base64) instead of auto-uploading
function FileUploadWithData({ onFileReady, kind }) {
  const [filename, setFilename] = useState(null);
  const [error, setError] = useState('');
  const maxMb = UPLOAD_KINDS[kind].maxMb;

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) { setError(`"${file.name}" is larger than ${maxMb} MB, which is the most this takes.`); return; }
    const refused = uploadRefusal(file.name, file.type, kind);
    if (refused) { setError(refused); return; }
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setFilename(file.name);
      setError('');
      onFileReady({ filename: file.name, content_type: file.type || 'application/octet-stream', data: base64, file_size: file.size });
    } catch { setError('Failed to read file'); }
  };

  return (
    <div>
      {filename ? (
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
          </svg>
          <span className="text-[9px] font-semibold text-emerald-700 flex-1 truncate">{filename}</span>
          <button type="button" onClick={() => { setFilename(null); onFileReady(null); }} className="text-emerald-400 hover:text-red-500 font-bold text-[11px]">×</button>
        </div>
      ) : (
        <label className="block border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-gold/60 hover:bg-gray-50 transition-all">
          <svg className="w-5 h-5 text-gray-300 mx-auto mb-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <span className="text-[9px] text-gray-400">Drag file here or click to browse</span>
          <input type="file" accept={acceptAttribute(kind)} className="hidden" onChange={handleChange} />
        </label>
      )}
      {error && <div role="alert" className="mt-1 px-2 py-1 text-[9px] text-red-700 bg-red-50 border border-red-200 rounded">{error}</div>}
    </div>
  );
}

// ── Tab 2: Accreditations ─────────────────────────────────────────────────────
function AccreditationsTab() {
  const [accreditations, setAccreditations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAcc, setEditAcc] = useState(null);
  const [createType, setCreateType] = useState(null);
  const { toast } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api.get('qms/accreditations').then(setAccreditations).catch(() => toast('Failed to load', 'error')).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const accByType = {};
  for (const a of accreditations) accByType[a.type] = a;

  const handleDelete = async (acc) => {
    if (!confirm(`Delete "${acc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`qms/accreditations/${acc.id}`);
      toast('Accreditation deleted', 'success');
      load();
    } catch (e) { toast(e.message, 'error'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setCreateType('other')} className={btnPrimary}>+ New Accreditation</button>
      </div>

      {loading ? (
        <div className="text-[11px] text-gray-400 py-8 text-center">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Known types first */}
          {KNOWN_ACCREDITATION_TYPES.map(meta => {
            const acc = accByType[meta.type];
            if (!acc) {
              return (
                <div key={meta.type} className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="font-bold text-[12px] text-gray-400">{meta.label}</div>
                    <div className="text-[10px] text-gray-400">{meta.sub}</div>
                  </div>
                  <button
                    onClick={() => setCreateType(meta.type)}
                    className="text-[9px] text-navy border border-navy/30 px-2.5 py-1 rounded-lg hover:bg-navy/5 font-semibold transition-colors"
                  >
                    Set up
                  </button>
                </div>
              );
            }
            return <AccreditationAdminCard key={acc.id} acc={acc} onEdit={() => setEditAcc(acc)} onDelete={() => handleDelete(acc)} />;
          })}
          {/* Other types not in known list */}
          {accreditations.filter(a => !KNOWN_ACCREDITATION_TYPES.find(m => m.type === a.type)).map(acc => (
            <AccreditationAdminCard key={acc.id} acc={acc} onEdit={() => setEditAcc(acc)} onDelete={() => handleDelete(acc)} />
          ))}
        </div>
      )}

      {editAcc && <AccreditationModal acc={editAcc} onClose={() => setEditAcc(null)} onSaved={() => { setEditAcc(null); load(); }} />}
      {createType !== null && (
        <AccreditationModal
          acc={null}
          defaultType={createType}
          onClose={() => setCreateType(null)}
          onSaved={() => { setCreateType(null); load(); }}
        />
      )}
    </div>
  );
}

function AccreditationAdminCard({ acc, onEdit, onDelete }) {
  const { toast } = useToast();
  const [showHistory, setShowHistory] = useState(false);
  const [audits, setAudits] = useState(null); // null = not yet fetched
  const [loadingAudits, setLoadingAudits] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const loadAudits = async () => {
    setLoadingAudits(true);
    try {
      const data = await api.get(`qms/accreditations/${acc.id}/audits`);
      setAudits(data || []);
    } catch { toast('Failed to load audit history', 'error'); }
    finally { setLoadingAudits(false); }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && audits === null) loadAudits();
  };

  const handleDeleteAudit = async (auditId) => {
    if (!confirm('Delete this audit summary? The file will also be removed.')) return;
    try {
      await api.delete(`qms/accreditations/${acc.id}/audits/${auditId}`);
      setAudits(prev => (prev || []).filter(a => a.id !== auditId));
    } catch (e) { toast(e.message || 'Delete failed', 'error'); }
  };

  const colors = accStatusColors(acc.computed_status);
  const auditCount = audits?.length ?? null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${colors.dot}`} />
            <span className="font-bold text-[13px] text-navy">{acc.name}</span>
          </div>
          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase ${colors.badge}`}>
            {acc.computed_status === 'due_soon' ? 'Due Soon' : acc.computed_status === 'expired' ? 'Expired' : 'Current'}
          </span>
        </div>
        {acc.certifying_body && <div className="text-[10px] text-gray-500">{acc.certifying_body}</div>}
        {acc.scope && <div className="text-[9px] text-gray-400 line-clamp-2">{acc.scope}</div>}
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[9px] text-gray-500">
          {acc.issue_date    && <><span className="text-gray-400 font-semibold">Issued</span><span>{formatDate(acc.issue_date)}</span></>}
          {acc.expiry_date   && <><span className="text-gray-400 font-semibold">Expires</span><span>{formatDate(acc.expiry_date)}</span></>}
          {acc.last_audit_date && <><span className="text-gray-400 font-semibold">Last Audit</span><span>{formatDate(acc.last_audit_date)}</span></>}
          {acc.next_audit_date && <><span className="text-gray-400 font-semibold">Next Audit</span><span>{formatDate(acc.next_audit_date)}</span></>}
          {acc.responsible_owner_name && <><span className="text-gray-400 font-semibold">Owner</span><span>{acc.responsible_owner_name}</span></>}
        </div>
        {acc.certificate_r2_key && (
          <a href={`/api/uploads/${acc.certificate_r2_key}`} target="_blank" rel="noopener noreferrer"
            className="text-[9px] text-gold hover:text-gold/80 font-semibold flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            View Certificate
          </a>
        )}
        <div className="flex gap-2 pt-1 flex-wrap">
          <button onClick={onEdit} className="text-[9px] text-navy border border-navy/30 px-2.5 py-1 rounded-lg hover:bg-navy/5 font-semibold transition-colors">Edit</button>
          <button onClick={onDelete} className="text-[9px] text-red-500 border border-red-200 px-2.5 py-1 rounded-lg hover:bg-red-50 font-semibold transition-colors">Delete</button>
          <button onClick={toggleHistory} className="text-[9px] text-gray-600 border border-gray-200 px-2.5 py-1 rounded-lg hover:bg-gray-50 font-semibold transition-colors flex items-center gap-1">
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Audit History{auditCount !== null ? ` (${auditCount})` : ''}
            <svg className={`w-2.5 h-2.5 transition-transform ${showHistory ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
        </div>
      </div>

      {showHistory && (
        <div className="border-t border-gray-100 bg-slate-50/60 px-4 py-3 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wide">Audit History</span>
            <button onClick={() => setShowUpload(true)} className={btnGold + ' text-[9px] py-1 px-2.5'}>+ Upload Summary</button>
          </div>
          {loadingAudits && <div className="text-[9px] text-gray-400 py-2 text-center">Loading…</div>}
          {!loadingAudits && audits !== null && audits.length === 0 && (
            <div className="text-[9px] text-gray-400 py-2 text-center italic">No audit summaries uploaded yet.</div>
          )}
          {!loadingAudits && (audits || []).map(a => {
            const om = auditOutcomeMeta(a.outcome);
            return (
              <div key={a.id} className="bg-white border border-gray-200 rounded-lg p-3 space-y-1.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-bold text-navy">{formatDate(a.audit_date)}</span>
                    {a.outcome && <span className={`text-[7.5px] font-bold px-1.5 py-0.5 rounded uppercase ${om.cls}`}>{om.label}</span>}
                    {a.auditor && <span className="text-[9px] text-gray-500">{a.auditor}</span>}
                  </div>
                  <button onClick={() => handleDeleteAudit(a.id)} className="text-red-300 hover:text-red-500 font-bold text-[13px] leading-none flex-shrink-0" title="Delete">×</button>
                </div>
                {a.findings && <p className="text-[9px] text-gray-600 leading-relaxed line-clamp-3">{a.findings}</p>}
                {a.r2_key && (
                  <a href={`/api/uploads/${a.r2_key}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[9px] text-gold hover:text-gold/80 font-semibold">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    {a.filename || 'Download Summary'}
                  </a>
                )}
                <div className="text-[7.5px] text-gray-400">Uploaded by {a.uploaded_by_name} · {formatDate(a.uploaded_at)}</div>
              </div>
            );
          })}
        </div>
      )}

      {showUpload && (
        <AuditSummaryModal
          acc={acc}
          onClose={() => setShowUpload(false)}
          onSaved={() => { setShowUpload(false); loadAudits(); }}
        />
      )}
    </div>
  );
}

function AuditSummaryModal({ acc, onClose, onSaved }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [fileData, setFileData] = useState(null);
  const [form, setForm] = useState({ audit_date: '', auditor: '', outcome: '', findings: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.audit_date) { toast('Audit date is required', 'error'); return; }
    setSaving(true);
    try {
      const body = { ...form };
      if (fileData) {
        body.filename     = fileData.filename;
        body.content_type = fileData.content_type;
        body.data         = fileData.data;
        body.file_size    = fileData.file_size;
      }
      await api.post(`qms/accreditations/${acc.id}/audits`, body);
      toast('Audit summary uploaded', 'success');
      onSaved();
    } catch (e) { setSaveError(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  return (
    <Modal title={`Upload Audit Summary — ${acc.name}`} onClose={onClose}
      footer={<><button onClick={onClose} className={btnSecondary}>Cancel</button><button onClick={handleSave} disabled={saving} className={btnPrimary}>{saving ? 'Uploading…' : 'Save'}</button></>}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Audit Date" required>
            <input type="date" className={inputCls} value={form.audit_date} onChange={e => set('audit_date', e.target.value)} />
          </Field>
          <Field label="Outcome">
            <select className={inputCls} value={form.outcome} onChange={e => set('outcome', e.target.value)}>
              <option value="">— Select —</option>
              {AUDIT_OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Auditor / Lead Assessor">
          <input className={inputCls} value={form.auditor} onChange={e => set('auditor', e.target.value)} placeholder="e.g. BSI UK Ltd — John Smith" />
        </Field>
        <Field label="Findings / Summary Notes">
          <textarea className={inputCls} rows={4} value={form.findings} onChange={e => set('findings', e.target.value)} placeholder="Key findings, observations, corrective actions required…" />
        </Field>
        <Field label="Audit Summary Document (PDF, Word, etc.)">
          <FileUploadWithData onFileReady={setFileData} kind="audit-report" />
        </Field>
        {saveError && <div role="alert" className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{saveError}</div>}
      </div>
    </Modal>
  );
}

function AccreditationModal({ acc, defaultType, onClose, onSaved }) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [fileData, setFileData] = useState(null);
  const [form, setForm] = useState({
    name: acc?.name || '',
    type: acc?.type || defaultType || 'other',
    certifying_body: acc?.certifying_body || '',
    scope: acc?.scope || '',
    issue_date: acc?.issue_date || '',
    expiry_date: acc?.expiry_date || '',
    next_audit_date: acc?.next_audit_date || '',
    last_audit_date: acc?.last_audit_date || '',
    responsible_owner_name: acc?.responsible_owner_name || '',
    notes: acc?.notes || '',
    sort_order: acc?.sort_order || 0,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name) { toast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      const body = { ...form };
      if (fileData) {
        body.filename = fileData.filename;
        body.content_type = fileData.content_type;
        body.data = fileData.data;
      }
      if (acc) {
        await api.put(`qms/accreditations/${acc.id}`, body);
        toast('Accreditation updated', 'success');
      } else {
        await api.post('qms/accreditations', body);
        toast('Accreditation created', 'success');
      }
      onSaved();
    } catch (e) { setSaveError(e.message || 'Save failed'); } finally { setSaving(false); }
  };

  return (
    <Modal title={acc ? `Edit — ${acc.name}` : 'New Accreditation'} onClose={onClose} wide
      footer={<><button onClick={onClose} className={btnSecondary}>Cancel</button><button onClick={handleSave} disabled={saving} className={btnPrimary}>{saving ? 'Saving…' : acc ? 'Save' : 'Create'}</button></>}
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" required><input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. ISO 9001" /></Field>
          <Field label="Type">
            <select className={inputCls} value={form.type} onChange={e => set('type', e.target.value)} disabled={!!acc}>
              {ACCREDITATION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Certifying Body"><input className={inputCls} value={form.certifying_body} onChange={e => set('certifying_body', e.target.value)} placeholder="e.g. BSI" /></Field>
          <Field label="Responsible Owner"><OwnerSelect className={inputCls} value={form.responsible_owner_name} onChange={v => set('responsible_owner_name', v)} /></Field>
        </div>
        <Field label="Scope"><textarea className={inputCls} rows={2} value={form.scope} onChange={e => set('scope', e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Issue Date"><input type="date" className={inputCls} value={form.issue_date} onChange={e => set('issue_date', e.target.value)} /></Field>
          <Field label="Expiry Date"><input type="date" className={inputCls} value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} /></Field>
          <Field label="Last Audit"><input type="date" className={inputCls} value={form.last_audit_date} onChange={e => set('last_audit_date', e.target.value)} /></Field>
          <Field label="Next Audit"><input type="date" className={inputCls} value={form.next_audit_date} onChange={e => set('next_audit_date', e.target.value)} /></Field>
        </div>
        <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></Field>
        <Field label="Certificate (PDF)">
          <FileUploadWithData onFileReady={setFileData} kind="certificate" />
          {acc?.certificate_filename && !fileData && (
            <div className="mt-1 text-[9px] text-gray-400">Current: {acc.certificate_filename}</div>
          )}
        </Field>
        {saveError && <div role="alert" className="text-[9px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{saveError}</div>}
      </div>
    </Modal>
  );
}

// ── Stats bar ─────────────────────────────────────────────────────────────────
// The counts are a snapshot, so anything that adds or removes a document has to
// say so. Deleting a register full of documents and leaving the header claiming
// they are still there reads as the delete having failed.
function useQmsStats(refreshKey) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get('qms/stats').then(setStats).catch(() => {});
  }, [refreshKey]);
  return stats;
}

function StatsBar({ refreshKey }) {
  const stats = useQmsStats(refreshKey);
  if (!stats) return null;
  const chips = [
    { label: 'Total docs',      value: stats.total_docs,       cls: 'bg-navy/5 text-navy' },
    { label: 'Live',            value: stats.live_docs,        cls: 'bg-emerald-50 text-emerald-700' },
    { label: 'Pending approval',value: stats.pending_approval, cls: stats.pending_approval > 0 ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-500' },
    { label: 'In review',       value: stats.in_review,        cls: stats.in_review > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-500' },
    { label: 'Overdue review',  value: stats.overdue_review,   cls: stats.overdue_review > 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-500' },
    { label: 'Due soon (90d)',  value: stats.due_soon_review,  cls: stats.due_soon_review > 0 ? 'bg-orange-50 text-orange-700' : 'bg-gray-50 text-gray-500' },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map(c => (
        <div key={c.label} className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${c.cls}`}>
          <span className="text-[18px] font-bold leading-none">{c.value}</span>
          <span className="text-[9px] font-semibold uppercase tracking-wide leading-tight">{c.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Tab 3: Review Queue ───────────────────────────────────────────────────────
function ReviewQueueTab() {
  const [tasks, setTasks]           = useState([]);
  const [docs, setDocs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const employees = useActiveEmployees();
  const { toast } = useToast();

  const load = useCallback(() => {
    Promise.all([
      api.get('qms/review-tasks'),
      api.get('qms/documents'),
    ]).then(([t, d]) => {
      setTasks(t || []);
      setDocs((d || []).filter(doc => doc.status === 'live'));
    }).catch(() => toast('Failed to load review tasks', 'error'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (task, newStatus) => {
    try {
      const updated = await api.put(`qms/review-tasks/${task.id}`, { status: newStatus });
      setTasks(prev => prev.map(t => t.id === task.id ? updated : t));
      toast('Task updated', 'success');
    } catch (e) {
      toast(e.message || 'Failed to update task', 'error');
    }
  };

  const handleDelete = async (taskId) => {
    if (!confirm('Delete this review task?')) return;
    try {
      await api.delete(`qms/review-tasks/${taskId}`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
      toast('Task deleted', 'success');
    } catch (e) {
      toast(e.message || 'Failed to delete', 'error');
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  const active = tasks.filter(t => t.status !== 'completed' && t.status !== 'cancelled');
  const done   = tasks.filter(t => t.status === 'completed' || t.status === 'cancelled');

  const taskStatusColor = (t) => {
    if (t.status === 'completed') return 'bg-emerald-100 text-emerald-700';
    if (t.status === 'cancelled') return 'bg-gray-100 text-gray-500';
    if (t.status === 'in_progress') return 'bg-blue-100 text-blue-700';
    if (t.due_date < today) return 'bg-red-100 text-red-700';
    const d = new Date(t.due_date);
    const soon = new Date(); soon.setDate(soon.getDate() + 30);
    if (d <= soon) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-500';
  };

  if (loading) return <div className="text-[11px] text-gray-400 py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[13px] font-bold text-navy">Document Review Queue</div>
          <div className="text-[10px] text-gray-500 mt-0.5">Track scheduled reviews, assign owners, and monitor progress</div>
        </div>
        <button onClick={() => setShowCreate(true)} className={btnPrimary}>+ Schedule Review</button>
      </div>

      {active.length === 0 && (
        <div className="text-[11px] text-gray-400 border border-dashed border-gray-200 rounded-xl py-10 text-center">
          No active review tasks. Schedule a review to start tracking.
        </div>
      )}

      {active.length > 0 && (
        <div className="space-y-2">
          {active.map(task => (
            <div key={task.id} className="border border-gray-200 rounded-xl bg-white overflow-hidden">
              <div className="flex items-start gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[8px] font-bold bg-navy/10 text-navy px-1.5 py-0.5 rounded font-mono">{task.doc_number}</span>
                    <span className="font-semibold text-[12px] text-navy">{task.doc_title}</span>
                    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase ${taskStatusColor(task)}`}>
                      {task.status === 'in_progress' ? 'In Progress' : task.due_date < today && task.status === 'pending' ? 'Overdue' : task.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-[9px] text-gray-500 flex-wrap">
                    <span>Due: <span className={task.due_date < today ? 'text-red-600 font-semibold' : ''}>{formatDate(task.due_date)}</span></span>
                    {task.assigned_to_name && <span>Assigned: {task.assigned_to_name}</span>}
                    {task.owner_name && <span>Doc owner: {task.owner_name}</span>}
                    {task.notes && <span className="italic truncate max-w-xs">{task.notes}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {task.status === 'pending' && (
                    <button
                      onClick={() => handleStatusChange(task, 'in_progress')}
                      className="text-[9px] px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50 transition-colors"
                    >
                      Start
                    </button>
                  )}
                  {(task.status === 'pending' || task.status === 'in_progress') && (
                    <button
                      onClick={() => handleStatusChange(task, 'completed')}
                      className="text-[9px] px-2 py-1 rounded bg-emerald-500 text-emerald-950 hover:bg-emerald-600 transition-colors"
                    >
                      Complete
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(task.id)}
                    className="text-[9px] px-2 py-1 rounded border border-red-100 text-red-500 hover:bg-red-50 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {done.length > 0 && (
        <details className="mt-4">
          <summary className="text-[9px] font-bold text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-600">
            Completed / Cancelled ({done.length})
          </summary>
          <div className="mt-2 space-y-1.5">
            {done.map(task => (
              <div key={task.id} className="flex items-center gap-3 px-4 py-2 border border-gray-100 rounded-lg bg-gray-50 text-[10px] text-gray-500">
                <span className="font-mono text-[8px] text-gray-400">{task.doc_number}</span>
                <span className="font-semibold text-gray-600">{task.doc_title}</span>
                <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${task.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {task.status}
                </span>
                {task.completed_at && <span>Completed {formatDate(task.completed_at)}</span>}
                {task.completed_by_name && <span>by {task.completed_by_name}</span>}
              </div>
            ))}
          </div>
        </details>
      )}

      {showCreate && (
        <CreateReviewTaskModal
          docs={docs}
          employees={employees}
          onClose={() => setShowCreate(false)}
          onCreated={task => { setTasks(prev => [task, ...prev]); setShowCreate(false); }}
        />
      )}
    </div>
  );
}

function CreateReviewTaskModal({ docs, employees, onClose, onCreated }) {
  const [docId, setDocId]         = useState('');
  const [assignee, setAssignee]   = useState('');
  const [dueDate, setDueDate]     = useState('');
  const [notes, setNotes]         = useState('');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const { toast } = useToast();

  const today = new Date();
  const defaultDue = new Date(today);
  defaultDue.setDate(today.getDate() + 30);
  const defaultDueStr = defaultDue.toISOString().slice(0, 10);

  const selectedDoc = docs.find(d => d.id === docId);
  const selectedEmp = employees.find(e => e.id === assignee);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!docId || !dueDate) { setError('Document and due date are required'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        document_id: docId,
        due_date: dueDate,
        notes: notes || undefined,
        assigned_to_id: selectedEmp?.id || undefined,
        assigned_to_name: selectedEmp ? `${selectedEmp.first_name} ${selectedEmp.last_name}`.trim() : undefined,
      };
      const task = await api.post('qms/review-tasks', payload);
      toast('Review task scheduled', 'success');
      onCreated(task);
    } catch (e) {
      setError(e.message || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Schedule Document Review" onClose={onClose} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Field label="Document" required>
          <select className={inputCls} value={docId} onChange={e => setDocId(e.target.value)} required>
            <option value="">— Select document —</option>
            {docs.map(d => (
              <option key={d.id} value={d.id}>{d.doc_number} — {d.title}</option>
            ))}
          </select>
        </Field>
        {selectedDoc?.next_review_date && (
          <div className="text-[9px] text-amber-700 bg-amber-50 px-2 py-1.5 rounded border border-amber-100">
            Document next review date: {formatDate(selectedDoc.next_review_date)}
          </div>
        )}
        <Field label="Due Date" required>
          <input type="date" className={inputCls} value={dueDate} onChange={e => setDueDate(e.target.value)}
            defaultValue={defaultDueStr} required />
        </Field>
        <Field label="Assigned To">
          <select className={inputCls} value={assignee} onChange={e => setAssignee(e.target.value)}>
            <option value="">— Unassigned —</option>
            {employees.map(p => (
              <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
            ))}
          </select>
        </Field>
        <Field label="Notes">
          <textarea className={inputCls} rows={2} value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Optional notes about this review..." />
        </Field>
        {error && <div className="text-[10px] text-red-600 bg-red-50 px-2 py-1.5 rounded">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={btnSecondary}>Cancel</button>
          <button type="submit" disabled={saving} className={btnPrimary}>
            {saving ? 'Saving…' : 'Schedule Review'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Tab 4: Compliance Matrix ──────────────────────────────────────────────────
function ComplianceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [filterState, setFilterState] = useState('all'); // all | outstanding | stale
  const { toast } = useToast();

  useEffect(() => {
    api.get('qms/compliance')
      .then(setData)
      .catch(() => toast('Failed to load compliance data', 'error'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[11px] text-gray-400 py-8 text-center">Loading…</div>;
  if (!data) return null;

  const { matrix, staff, exempt_staff } = data;

  if ((matrix || []).length === 0) {
    return (
      <div className="text-[11px] text-gray-400 py-8 text-center">
        No live documents require acknowledgement. Set <em>Requires Acknowledgement</em> on a live document to see the matrix here.
      </div>
    );
  }

  const totalStaff = (staff || []).length;

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-[9px] text-gray-500">
        <div className="flex items-center gap-1">
          <svg className="w-3 h-3 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          Acknowledged (current version)
        </div>
        <div className="flex items-center gap-1">
          <span className="text-amber-500 font-bold text-[11px]">⏰</span>
          Acknowledged old version — needs refresh
        </div>
        <div className="flex items-center gap-1">
          <svg className="w-3 h-3 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
          Not yet read
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 flex-wrap">
        {[
          { key: 'all', label: 'All documents' },
          { key: 'outstanding', label: 'Has outstanding' },
          { key: 'stale', label: 'Has stale reads' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilterState(f.key)}
            className={`text-[9px] font-bold px-2.5 py-1 rounded-full border transition-colors ${filterState === f.key ? 'bg-navy text-white border-navy' : 'border-gray-300 text-gray-500 hover:border-navy hover:text-navy'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {matrix.filter(doc => {
        if (filterState === 'all') return true;
        const readMap = {};
        for (const r of (doc.reads || [])) readMap[r.person_id] = r;
        const currentAck = (staff || []).filter(s => readMap[s.id]?.version_id === doc.current_version_id).length;
        const staleAck   = (staff || []).filter(s => readMap[s.id] && readMap[s.id].version_id !== doc.current_version_id).length;
        if (filterState === 'outstanding') return currentAck < totalStaff - staleAck;
        if (filterState === 'stale') return staleAck > 0;
        return true;
      }).map(doc => {
        const isExpanded = expandedDoc === doc.id;
        const readMap = {};
        for (const r of (doc.reads || [])) readMap[r.person_id] = r;

        const currentAck = (staff || []).filter(s => readMap[s.id]?.version_id === doc.current_version_id);
        const staleAck   = (staff || []).filter(s => readMap[s.id] && readMap[s.id].version_id !== doc.current_version_id);
        const notRead    = (staff || []).filter(s => !readMap[s.id]);
        const pct = totalStaff ? Math.round((currentAck.length / totalStaff) * 100) : 0;

        return (
          <div key={doc.id} className="border border-gray-200 rounded-xl overflow-hidden">
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[8px] font-bold bg-navy/10 text-navy px-1.5 py-0.5 rounded font-mono">{doc.doc_number}</span>
                  <span className="font-semibold text-[12px] text-navy">{doc.title}</span>
                  {doc.version_number && <span className="text-[8px] text-gray-400 font-mono">v{doc.version_number}</span>}
                  <span className="text-[8px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 capitalize">{doc.category}</span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-xs">
                    <div className={`h-full rounded-full transition-all ${pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-gold' : 'bg-amber-400'}`}
                      style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-[9px] text-gray-500 flex-shrink-0">
                    {currentAck.length} / {totalStaff} current
                    {staleAck.length > 0 && <span className="text-amber-500 ml-1">· {staleAck.length} stale</span>}
                    {notRead.length > 0 && <span className="text-gray-400 ml-1">· {notRead.length} unread</span>}
                  </span>
                </div>
              </div>
              <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>

            {isExpanded && (
              <div className="border-t border-gray-100 px-4 py-3 bg-gray-50 space-y-3">
                {currentAck.length > 0 && (
                  <div>
                    <div className="text-[8px] font-bold text-emerald-600 uppercase tracking-wider mb-1.5">Acknowledged (current version)</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                      {currentAck.map(s => {
                        const r = readMap[s.id];
                        return (
                          <div key={s.id} className="flex items-center gap-1.5 text-[9px] text-emerald-700">
                            <svg className="w-3 h-3 flex-shrink-0 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                            <span className="truncate">{s.name}</span>
                            <span className="text-gray-400 flex-shrink-0">{formatDate(r.read_at)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {staleAck.length > 0 && (
                  <div>
                    <div className="text-[8px] font-bold text-amber-600 uppercase tracking-wider mb-1.5">Acknowledged old version — needs refresh</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                      {staleAck.map(s => {
                        const r = readMap[s.id];
                        return (
                          <div key={s.id} className="flex items-center gap-1.5 text-[9px] text-amber-700">
                            <span className="text-[11px]">⏰</span>
                            <span className="truncate">{s.name}</span>
                            <span className="text-gray-400 flex-shrink-0">{formatDate(r.read_at)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {notRead.length > 0 && (
                  <div>
                    <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Not yet read</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                      {notRead.map(s => (
                        <div key={s.id} className="flex items-center gap-1.5 text-[9px] text-gray-400">
                          <svg className="w-3 h-3 flex-shrink-0 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                          </svg>
                          <span className="truncate">{s.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(exempt_staff || []).length > 0 && (
                  <div className="pt-1 border-t border-gray-100">
                    <div className="text-[8px] font-bold text-gray-300 uppercase tracking-wider mb-1.5">Exempt from QMS acknowledgement</div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1">
                      {(exempt_staff || []).map(s => (
                        <span key={s.id} className="text-[9px] text-gray-300 italic">{s.name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Tab 5: Bulk Import ────────────────────────────────────────────────────────
function BulkImportTab() {
  const [docs, setDocs] = useState([]);
  const [items, setItems] = useState([]);
  const [autoApprove, setAutoApprove] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [dragging, setDragging] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    api.get('qms/documents').then(setDocs).catch(() => {});
  }, []);

  function extractVersion(name) {
    const stem = name.replace(/\.[^.]+$/, '');
    let m = stem.match(/[Vv](\d+(?:\.\d+)?)\s*$/);
    if (m) { const n = m[1]; return n.includes('.') ? n : `${n}.0`; }
    m = stem.match(/\b(20\d{2})\b/);
    if (m) return `${m[1]}.1`;
    return '1.0';
  }

  function scoreFile(name, doc) {
    const stem = name.toLowerCase().replace(/\.[^.]+$/, '');
    const numMatch = stem.match(/\b([a-z]{2,5}-[a-z]-\d+)\b/i);
    if (numMatch && doc.doc_number && numMatch[1].toLowerCase() === doc.doc_number.toLowerCase()) return 95;
    const ignore = new Set(['the','and','for','with','our','all','any','this','that','are','was','its']);
    const tok = s => s.toLowerCase().replace(/[-_]+/g,' ').replace(/[^a-z0-9 ]/g,' ').split(/\s+/).filter(t => t.length > 2 && !ignore.has(t));
    const nameTokens = tok(stem);
    const docSet = new Set(tok(doc.doc_number + ' ' + doc.title));
    if (!nameTokens.length) return 0;
    return Math.round((nameTokens.filter(t => docSet.has(t)).length / nameTokens.length) * 85);
  }

  function bestMatch(filename) {
    if (!docs.length) return { docId: '', confidence: 0 };
    let best = { docId: '', confidence: 0 };
    for (const doc of docs) {
      const s = scoreFile(filename, doc);
      if (s > best.confidence) best = { docId: doc.id, confidence: s };
    }
    return best;
  }

  async function processFiles(fileList) {
    const newItems = [];
    for (const file of Array.from(fileList)) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['pdf', 'docx', 'doc'].includes(ext)) continue;
      if (file.size > 70 * 1024 * 1024) { toast(`${file.name}: too large (max 70 MB)`, 'error'); continue; }
      let base64;
      try {
        base64 = await new Promise((res, rej) => {
          const r = new FileReader();
          r.onload = () => res(r.result.split(',')[1]);
          r.onerror = rej;
          r.readAsDataURL(file);
        });
      } catch { toast(`Failed to read ${file.name}`, 'error'); continue; }
      const role = ext === 'pdf' ? 'pdf' : 'word';
      const { docId, confidence } = bestMatch(file.name);
      newItems.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: file.name, ext, base64, size: file.size,
        contentType: file.type || (ext === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'),
        matchedDocId: docId, role,
        version: extractVersion(file.name),
        status: 'pending', error: null, confidence,
      });
    }
    setItems(prev => [...prev, ...newItems]);
  }

  function updateItem(id, patch) {
    setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
  }

  async function handleUploadAll() {
    const pending = items.filter(it => it.status === 'pending' && it.role !== 'skip' && it.matchedDocId);
    if (!pending.length) { toast('No items ready to upload', 'error'); return; }
    setUploading(true); setSummary(null);
    const groups = {};
    for (const it of pending) { if (!groups[it.matchedDocId]) groups[it.matchedDocId] = []; groups[it.matchedDocId].push(it); }
    let done = 0, errs = 0;
    for (const [docId, group] of Object.entries(groups)) {
      const pdf  = group.find(i => i.role === 'pdf');
      const word = group.find(i => i.role === 'word');
      const primary = pdf || word;
      setItems(prev => prev.map(it => group.some(g => g.id === it.id) ? { ...it, status: 'uploading' } : it));
      try {
        const body = { version_number: primary.version, change_summary: 'Bulk import' };
        if (pdf)  { body.filename = pdf.name;   body.content_type = pdf.contentType;   body.data = pdf.base64;   body.file_size = pdf.size; }
        if (word) { body.word_filename = word.name; body.word_content_type = word.contentType; body.word_data = word.base64; body.word_file_size = word.size; }
        const result = await api.post(`qms/documents/${docId}/versions`, body);
        if (autoApprove && result?.id) await api.put(`qms/documents/${docId}/versions/${result.id}/approve`, {});
        setItems(prev => prev.map(it => group.some(g => g.id === it.id) ? { ...it, status: 'done' } : it));
        done++;
      } catch (e) {
        setItems(prev => prev.map(it => group.some(g => g.id === it.id) ? { ...it, status: 'error', error: e.message } : it));
        errs++;
      }
    }
    setUploading(false); setSummary({ done, errs });
  }

  const pendingCount = items.filter(i => i.status === 'pending' && i.role !== 'skip' && i.matchedDocId).length;
  const statusMeta = {
    pending:   { label: 'Ready',      cls: 'bg-gray-100 text-gray-600' },
    uploading: { label: 'Uploading…', cls: 'bg-blue-100 text-blue-700' },
    done:      { label: 'Done',       cls: 'bg-emerald-100 text-emerald-700' },
    error:     { label: 'Error',      cls: 'bg-red-100 text-red-700' },
  };

  return (
    <div className="space-y-5">
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
        onDrop={e => { e.preventDefault(); setDragging(false); processFiles(e.dataTransfer.files); }}
        className={`rounded-xl border-2 border-dashed p-10 text-center transition-all ${dragging ? 'border-gold bg-gold/5' : 'border-gray-200 hover:border-gray-300 bg-gray-50/40'}`}
      >
        <svg className="w-8 h-8 text-gray-300 mx-auto mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <p className="text-[12px] font-semibold text-gray-600 mb-1">Drop PDF and Word files here</p>
        <p className="text-[10px] text-gray-400 mb-3">Each file is auto-matched to the right QMS document — review the table before uploading</p>
        <label className={`${btnSecondary} cursor-pointer inline-block`}>
          Browse files
          <input type="file" multiple accept=".pdf,.doc,.docx" className="hidden"
            onChange={e => { processFiles(e.target.files); e.target.value = ''; }} />
        </label>
      </div>

      {items.length > 0 && (
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-[10px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide text-[8px]">File</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide text-[8px]">Role</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide text-[8px]">QMS Document</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide text-[8px]">Version</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-500 uppercase tracking-wide text-[8px]">Status</th>
                  <th className="px-2 py-2 w-6"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map(item => {
                  const meta = statusMeta[item.status] || statusMeta.pending;
                  const busy = uploading || item.status === 'uploading';
                  const skipped = item.role === 'skip';
                  return (
                    <tr key={item.id} className={`${item.status === 'done' ? 'bg-emerald-50/40' : item.status === 'error' ? 'bg-red-50/40' : skipped ? 'opacity-40' : ''}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase flex-shrink-0 ${item.ext === 'pdf' ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>{item.ext}</span>
                          <span className="text-gray-700 truncate max-w-[180px]" title={item.name}>{item.name}</span>
                        </div>
                        {item.error && <div className="text-[8px] text-red-500 mt-0.5 truncate max-w-[220px]">{item.error}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <select disabled={busy} value={item.role}
                          onChange={e => updateItem(item.id, { role: e.target.value })}
                          className="border border-gray-200 rounded px-1.5 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy/30 disabled:opacity-50 bg-white">
                          <option value="pdf">PDF</option>
                          <option value="word">Word</option>
                          <option value="skip">Skip</option>
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span title={`${item.confidence}% match confidence`}
                            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.confidence >= 70 ? 'bg-emerald-500' : item.confidence >= 40 ? 'bg-amber-400' : 'bg-red-400'}`} />
                          <select disabled={busy} value={item.matchedDocId}
                            onChange={e => updateItem(item.id, { matchedDocId: e.target.value })}
                            className="border border-gray-200 rounded px-1.5 py-0.5 text-[10px] focus:outline-none focus:ring-1 focus:ring-navy/30 disabled:opacity-50 bg-white max-w-[230px]">
                            <option value="">— No match —</option>
                            {docs.map(d => <option key={d.id} value={d.id}>{d.doc_number} — {d.title}</option>)}
                          </select>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <input disabled={busy} value={item.version}
                          onChange={e => updateItem(item.id, { version: e.target.value })}
                          className="border border-gray-200 rounded px-1.5 py-0.5 text-[10px] w-14 focus:outline-none focus:ring-1 focus:ring-navy/30 disabled:opacity-50"
                          placeholder="1.0" />
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[8px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button disabled={busy} onClick={() => setItems(prev => prev.filter(it => it.id !== item.id))}
                          className="text-gray-300 hover:text-red-400 disabled:opacity-30 transition-colors text-[12px] leading-none">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-4 flex-wrap">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" checked={autoApprove} onChange={e => setAutoApprove(e.target.checked)} className="rounded" />
              <span className="text-[10px] text-gray-600">Auto-approve and publish immediately</span>
              {!autoApprove && <span className="text-[9px] text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Versions will need manual approval in the Review Queue tab</span>}
            </label>
            <div className="flex items-center gap-2">
              {summary && (
                <span className={`text-[10px] font-semibold ${summary.errs ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {summary.done} uploaded{summary.errs ? `, ${summary.errs} failed` : ' successfully'}
                </span>
              )}
              <button onClick={() => { setItems([]); setSummary(null); }} disabled={uploading} className={btnSecondary}>Clear all</button>
              <button onClick={handleUploadAll} disabled={uploading || pendingCount === 0} className={btnPrimary}>
                {uploading ? 'Uploading…' : `Upload ${pendingCount} document${pendingCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'documents',      label: 'Documents' },
  { id: 'accreditations', label: 'Accreditations' },
  { id: 'review-queue',   label: 'Review Queue' },
  { id: 'compliance',     label: 'Compliance Matrix' },
  { id: 'bulk-import',    label: 'Bulk Import' },
];

export default function QmsAdminPage() {
  const [registerVersion, setRegisterVersion] = useState(0);
  const [activeTab, setActiveTab] = useState('documents');
  // Guided tour of the Documents tab: auto-opens once per person, replayable.
  const tour = useFeatureTour(QMS_CONTROL_TOUR_ID);
  useEffect(() => { if (tour.open) setActiveTab('documents'); }, [tour.open]);

  return (
    <div className="px-4 py-6 space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-navy">QMS Control Panel</h1>
            <p className="text-[11px] text-gray-500 mt-0.5">Manage quality documents, accreditations and acknowledgement compliance</p>
          </div>
          <button onClick={tour.start} className="text-[10px] text-gray-500 hover:text-navy border border-gray-200 hover:border-navy rounded-lg px-2.5 py-1 transition-colors" title="How editing, approving and publishing a policy works">
            Take the tour
          </button>
        </div>
        <StatsBar refreshKey={registerVersion} />
      </div>
      {tour.open && <FeatureTour steps={qmsControlTourSteps} onFinish={tour.finish} onSkip={tour.skip} />}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-[11px] font-semibold border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-gold text-navy'
                : 'border-transparent text-gray-500 hover:text-navy'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'documents'      && <DocumentsTab onDocumentsChanged={() => setRegisterVersion(v => v + 1)} />}
        {activeTab === 'accreditations' && <AccreditationsTab />}
        {activeTab === 'review-queue'   && <ReviewQueueTab />}
        {activeTab === 'compliance'     && <ComplianceTab />}
        {activeTab === 'bulk-import'    && <BulkImportTab />}
      </div>
    </div>
  );
}
